import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  message,
  Dropdown,
  Menu,
  Space,
  Input,
  Popover,
  Skeleton,
  Tag,
  Select,
  Segmented,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
  UpOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSubmittals,
  deleteSubmittal,
  getSubmittalById,
  updateSubmittal,
  archiveSubmittal,
  downloadSubmittalAttachments,
  notifyProjectUsersAboutSubmittal,
  getSubmittalDefaultNotifyRecipients,
} from "../services/submittalsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateSubmittalModal from "../components/CreateSubmittalModal";
import UpdateSubmittalModal from "../components/UpdateSubmittalModal";
import ViewSubmittalModal from "../components/ViewSubmittalModal";
import NotifyUsersModal from "../components/NotifyUsersModal";
import ReviewStatusTag from "../components/ReviewStatusTag";
import Highlighter from "react-highlight-words";
import { exportSubmittalsToPDF } from "../utils/pdfUtils";
import { downloadCSV, downloadBlob } from "../utils/downloadUtils";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import SortableHeader from "../components/SortableHeader";
import ColumnSelectorPopover from "../components/ColumnSelectorPopover";
import {
  buildFullColumnOrder,
  getVisibleColumnsFromOrder,
  normalizeColumnSelectionOrder,
  orderColumnsByKeys,
  pinKeyLast,
} from "../utils/columnOrderUtils";
import {
  getPreferredPageSize,
  savePreferredPageSize,
} from "../utils/paginationUtils";
import { formatDate, formatDateTimeLocal } from "../utils/dateUtils";
import {
  getModuleSettings,
  saveModuleSettings,
  clearModuleSettings,
} from "../utils/filterStorageUtils";
import { PAYMENT_PROJECTS_LIST } from "../constants/paymentConstants";
import {
  getAvailableSubmittalColumns,
  getAvailableSubmittalFilters,
  getAvailableSubmittalSortField,
  getDefaultSubmittalColumns,
  getInitialSubmittalColumns,
  normalizeBooleanValue,
  MATERIAL_RELEASE_COLUMNS,
} from "../utils/projectUtils";
import {
  applyDateFiltersToQueryParams,
  createGetColumnDateRangeProps,
} from "../utils/dateFilterUtils";
import QuickFilterButtons from "../components/QuickFilterButtons";
import DueDatePill from "../components/DueDatePill";
import { toggleQuickFilter } from "../utils/quickFilterUtils";

const BASE_VISIBLE_COLUMNS = [
  "formatted_spec_section",
  "formatted_number",
  "revision",
  "title",
  "formatted_received_from",
  "ball_in_court",
  "status",
  "review_status",
  "priority",
];

const SUBMITTAL_COLUMN_OPTIONS = [
  { key: "formatted_spec_section", label: "Spec Section" },
  { key: "formatted_number", label: "Number" },
  { key: "revision", label: "Rev" },
  { key: "title", label: "Title" },
  { key: "submitted_for", label: "Submitted For" },
  { key: "formatted_received_from", label: "From" },
  { key: "ball_in_court", label: "Ball in Court" },
  { key: "issue_date", label: "Issue Date" },
  { key: "due_date", label: "Due Date" },
  { key: "most_recent_response_date", label: "Most Rec. Resp. Date" },
  { key: "released_on", label: "Released On" },
  { key: "estimated_delivery_date", label: "Est. Delivery" },
  { key: "status", label: "Status" },
  { key: "closed_at_date", label: "Closed Date" },
  { key: "review_status", label: "Review Status" },
  { key: "priority", label: "Priority" },
  { key: "edms", label: "EDMS" },
];

const SUBMITTAL_DATE_FILTER_KEYS = [
  "issue_date",
  "due_date",
  "released_on",
  "estimated_delivery_date",
  "closed_at_date",
];

const Submittals = () => {
  const {
    selectedProject,
    setSelectedProject,
    magicLoginActive,
    logout,
    currentUser,
    setCurrentUser,
  } = useAuth();
  const projectId = selectedProject.project_id;
  const projectName = selectedProject.project_name;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const isSpecialProject =
    selectedProject?.project_id === "68f8f57cfa4f140afe55ab64";
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedSubmittalId, setSelectedSubmittalId] = useState(null);
  const [revisionInitialData, setRevisionInitialData] = useState(null);
  const [isNotifyUsersModalVisible, setIsNotifyUsersModalVisible] =
    useState(false);
  const [submittalToNotify, setSubmittalToNotify] = useState(null);
  const [notifyDefaultUserIds, setNotifyDefaultUserIds] = useState([]);
  const [notifyDefaultUserIdsLoading, setNotifyDefaultUserIdsLoading] =
    useState(false);
  const notifyDefaultRecipientsFetchSeqRef = useRef(0);
  const [notifyingSubmittalId, setNotifyingSubmittalId] = useState(null);
  const queryClient = useQueryClient();
  const savedSettings = getModuleSettings(projectId, "submittals");
  const [searchedColumns, setSearchedColumns] = useState(
    savedSettings.searchedColumns || [],
  );
  const searchInput = useRef(null);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("submittals"),
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const submittalId = searchParams.get("submittal_id");
  const projectIdFromUrl = searchParams.get("project_id");
  const magicEmailDeepLinkConsumedRef = useRef(false);
  const userProjectIds =
    currentUser?.projects?.map((p) => p.project_id).join(",") ?? "";
  const isMagicLogin = localStorage.getItem("login_type") === "magic_login";
  const location = useLocation();
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [filters, setFilters] = useState(savedSettings.filters || {});
  const [searchTexts, setSearchTexts] = useState(
    savedSettings.searchTexts || {},
  );
  const [masterSearch, setMasterSearch] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("active");
  const trackMaterialRelease = normalizeBooleanValue(
    selectedProject?.track_material_release,
  );
  const defaultVisibleColumns = useMemo(
    () =>
      getDefaultSubmittalColumns(
        [
          ...BASE_VISIBLE_COLUMNS,
          ...(selectedProject?.use_custom_terminology ? ["edms"] : []),
        ],
        trackMaterialRelease,
      ),
    [selectedProject?.use_custom_terminology, trackMaterialRelease],
  );
  const selectableColumnOptions = useMemo(
    () =>
      SUBMITTAL_COLUMN_OPTIONS.filter((column) => {
        if (
          !trackMaterialRelease &&
          MATERIAL_RELEASE_COLUMNS.includes(column.key)
        ) {
          return false;
        }
        if (!selectedProject?.use_custom_terminology && column.key === "edms") {
          return false;
        }
        return true;
      }),
    [selectedProject?.use_custom_terminology, trackMaterialRelease],
  );
  const selectableColumnKeys = useMemo(
    () => selectableColumnOptions.map((column) => column.key),
    [selectableColumnOptions],
  );
  const initialVisible = useMemo(
    () =>
      normalizeColumnSelectionOrder(
        getInitialSubmittalColumns({
          savedColumns: currentUser?.submittal_columns,
          defaultColumns: defaultVisibleColumns,
          enabled: trackMaterialRelease,
        }),
        selectableColumnKeys,
      ),
    [
      currentUser?.submittal_columns,
      defaultVisibleColumns,
      selectableColumnKeys,
      trackMaterialRelease,
    ],
  );
  const [visibleColumns, setVisibleColumns] = useState(initialVisible);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [tempOrderedColumns, setTempOrderedColumns] = useState(visibleColumns);
  const [tableKey, setTableKey] = useState(0);
  const lastLoadedProjectId = useRef(projectId);
  const [loadedSettingsProjectId, setLoadedSettingsProjectId] =
    useState(projectId);
  const [sortField, setSortField] = useState(savedSettings.sortField || null);
  const [sortOrder, setSortOrder] = useState(savedSettings.sortOrder || "asc");
  const availableFilters = useMemo(
    () => getAvailableSubmittalFilters(filters, trackMaterialRelease),
    [filters, trackMaterialRelease],
  );
  const availableSortField = useMemo(
    () => getAvailableSubmittalSortField(sortField, trackMaterialRelease),
    [sortField, trackMaterialRelease],
  );
  const getColumnDateRangeProps = createGetColumnDateRangeProps(
    filters,
    setFilters,
  );
  const buildSubmittalFiltersForAPI = useCallback(
    (filterSource) => ({
      ...applyDateFiltersToQueryParams(
        filterSource,
        SUBMITTAL_DATE_FILTER_KEYS,
      ),
      ...(selectedProject?.use_custom_terminology && {
        type: "Submittal",
      }),
    }),
    [selectedProject?.use_custom_terminology],
  );
  const apiFilters = useMemo(
    () => buildSubmittalFiltersForAPI(availableFilters),
    [availableFilters, buildSubmittalFiltersForAPI],
  );

  const {
    data: submittalData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "submittals",
      projectId,
      pagination.current,
      pagination.pageSize,
      apiFilters,
      masterSearch,
      availableSortField,
      sortOrder,
      visibleColumns.includes("most_recent_response_date"),
      archivedFilter,
    ],

    queryFn: () =>
      fetchSubmittals(
        projectId,
        pagination.current,
        pagination.pageSize,
        apiFilters,
        masterSearch,
        availableSortField,
        sortOrder,
        false,
        visibleColumns.includes("most_recent_response_date"),
        archivedFilter,
      ),
    enabled: !!projectId && loadedSettingsProjectId === projectId,
    keepPreviousData: true,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [availableFilters, masterSearch, archivedFilter]);

  // Persist filters and sorts to localStorage
  useEffect(() => {
    if (lastLoadedProjectId.current === projectId) {
      saveModuleSettings(projectId, "submittals", {
        filters,
        searchTexts,
        searchedColumns,
        sortField,
        sortOrder,
      });
    }
  }, [filters, searchTexts, searchedColumns, sortField, sortOrder, projectId]);

  // Load settings when projectId changes
  useEffect(() => {
    const settings = getModuleSettings(projectId, "submittals");
    setFilters(settings.filters || {});
    setSearchTexts(settings.searchTexts || {});
    setSearchedColumns(settings.searchedColumns || []);
    setSortField(settings.sortField || null);
    setSortOrder(settings.sortOrder || "asc");
    setPagination((prev) => ({ ...prev, current: 1 }));
    lastLoadedProjectId.current = projectId;
    setLoadedSettingsProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    const nextColumns = normalizeColumnSelectionOrder(
      getInitialSubmittalColumns({
        savedColumns: currentUser?.submittal_columns,
        defaultColumns: defaultVisibleColumns,
        enabled: trackMaterialRelease,
      }),
      selectableColumnKeys,
    );
    setVisibleColumns(nextColumns);
    setTempSelection(nextColumns);
  }, [
    currentUser?.submittal_columns,
    defaultVisibleColumns,
    selectableColumnKeys,
    trackMaterialRelease,
  ]);

  useEffect(() => {
    if (trackMaterialRelease) {
      return;
    }

    setFilters((prev) => getAvailableSubmittalFilters(prev, false));
    setSearchTexts((prev) => getAvailableSubmittalFilters(prev, false));
    setSearchedColumns((prev) =>
      prev.filter((column) => !MATERIAL_RELEASE_COLUMNS.includes(column)),
    );
    setSortField((prev) => getAvailableSubmittalSortField(prev, false));
  }, [trackMaterialRelease]);

  const columnSetMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (data, variables) => {
      const user = await fetchCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
      const nextColumns = normalizeColumnSelectionOrder(
        variables.submittal_columns,
        selectableColumnKeys,
      );
      setVisibleColumns(nextColumns);
      setTempSelection(nextColumns);
      setIsPopoverOpen(false);
      setIsLoadingColumns(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update columns",
        description: error.message,
      });
      setIsLoadingColumns(false);
    },
  });

  const initializeColumnSelector = () => {
    setTempSelection(visibleColumns);
    setTempOrderedColumns(
      buildFullColumnOrder(visibleColumns, selectableColumnKeys),
    );
  };

  const handleSave = () => {
    const columnsToSave = normalizeColumnSelectionOrder(
      getAvailableSubmittalColumns(
        getVisibleColumnsFromOrder(tempOrderedColumns, tempSelection),
        trackMaterialRelease,
      ),
      selectableColumnKeys,
    );
    setIsLoadingColumns(true);
    columnSetMutation.mutate({
      ...currentUser,
      submittal_columns: columnsToSave,
    });
  };

  const handleCancel = () => {
    setTempSelection(visibleColumns);
    setTempOrderedColumns(
      buildFullColumnOrder(visibleColumns, selectableColumnKeys),
    );
    setIsPopoverOpen(false);
  };

  const handleExportPDF = async () => {
    try {
      if (!submittalData || submittalData.total === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });
      const allSubmittalsData = await fetchSubmittals(
        projectId,
        1,
        submittalData.total,
        buildSubmittalFiltersForAPI(availableFilters),
        masterSearch,
        availableSortField,
        sortOrder,
        true,
        // The submittal PDF always includes most_recent_response_date, so fetch
        // that data for the export even when the column is hidden in the table.
        true,
        archivedFilter,
      );

      await exportSubmittalsToPDF(
        allSubmittalsData.submittals,
        projectName,
        "Submittals",
        {
          visibleColumns,
          columnOrder: visibleColumns,
          selectedProject,
        },
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleDownloadSpecialTable = async () => {
    if (!isSpecialProject) return;
    try {
      if (!submittalData || submittalData.total === 0) {
        notification.warning({ message: "No data to download" });
        return;
      }

      notification.info({
        message: "Preparing download...",
        description: "Gathering all submittals for this project.",
        duration: 2,
      });

      const allSubmittalsData = await fetchSubmittals(
        projectId,
        1,
        submittalData.total,
        buildSubmittalFiltersForAPI(availableFilters),
        masterSearch,
        availableSortField,
        sortOrder,
        false,
        false,
        archivedFilter,
      );

      if (!allSubmittalsData?.submittals?.length) {
        notification.warning({ message: "No submittals found to download" });
        return;
      }

      const headers = [
        "Submittal Title",
        "Issue Date",
        "Revision Number",
        "Submittal Number",
      ];

      const rows = allSubmittalsData.submittals.map((submittal) => [
        submittal.title || "-",
        submittal.issue_date ? formatDate(submittal.issue_date) : "-",
        submittal.revision ?? "-",
        submittal.number ?? "-",
      ]);

      downloadCSV(headers, rows, `${projectName || "project"}-submittals.csv`);

      notification.success({ message: "Table downloaded successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to download table",
        description: error.message,
      });
    }
  };

  useEffect(() => {
    if (!projectIdFromUrl || !currentUser?.projects?.length) return;
    if (selectedProject?.project_id === projectIdFromUrl) return;
    const match = currentUser.projects.find(
      (p) => p.project_id === projectIdFromUrl,
    );
    if (match) {
      setSelectedProject(match);
    }
  }, [
    projectIdFromUrl,
    userProjectIds,
    selectedProject?.project_id,
    setSelectedProject,
  ]);

  useEffect(() => {
    const isMagic = magicLoginActive || isMagicLogin;
    if (
      isMagic &&
      location.pathname === "/submittals" &&
      !submittalId &&
      submittalData?.submittals?.length > 0 &&
      !magicEmailDeepLinkConsumedRef.current
    ) {
      notification.warning({
        message: "Access Restricted",
        description:
          "Your access is limited when using mail login link. Please log in normally for full access.",
      });
      logout();
      navigate("/login");
    }
  }, [
    magicLoginActive,
    isMagicLogin,
    submittalId,
    submittalData,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (submittalId) {
      setSelectedSubmittalId(submittalId);
      setIsViewModalVisible(true);
      magicEmailDeepLinkConsumedRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.delete("submittal_id");
      next.delete("project_id");
      setSearchParams(next, { replace: true });
    }
  }, [submittalId, searchParams, setSearchParams]);

  const deleteMutation = useMutation({
    mutationFn: deleteSubmittal,
    onSuccess: () => {
      notification.success({ message: "Submittal deleted successfully" });
      return queryClient.invalidateQueries(["submittals", projectId]);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete submittal",
        description: error.message,
      });
    },
  });

  if (isError) {
    notification.error({
      message: "Failed to load submittals",
      description: error.message,
    });
  }

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "submittals");
  };

  const handleUpdateClick = async (submittal) => {
    await setSelectedSubmittalId(submittal?.id);
    setIsUpdateModalVisible(true);
  };

  const handleViewClick = async (submittal) => {
    await setSelectedSubmittalId(submittal);
    setIsViewModalVisible(true);
  };

  const handleDeleteClick = (submittalId) => {
    deleteMutation.mutate(submittalId);
  };

  const handleNotifyUsers = async (submittal) => {
    const fetchSeq = ++notifyDefaultRecipientsFetchSeqRef.current;

    setSubmittalToNotify(submittal);
    setNotifyDefaultUserIds([]);
    setNotifyDefaultUserIdsLoading(true);
    setIsNotifyUsersModalVisible(true);

    try {
      const userIds = await getSubmittalDefaultNotifyRecipients(submittal.id);
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds(userIds);
    } catch {
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds([]);
    } finally {
      if (fetchSeq === notifyDefaultRecipientsFetchSeqRef.current) {
        setNotifyDefaultUserIdsLoading(false);
      }
    }
  };

  const handleSendNotification = async (userIds) => {
    if (!submittalToNotify) return;

    setNotifyingSubmittalId(submittalToNotify.id);
    setIsNotifyUsersModalVisible(false);

    try {
      const result = await notifyProjectUsersAboutSubmittal(
        submittalToNotify.id,
        userIds,
      );
      notification.success({
        message: "Notification sent successfully",
        description: `Email sent to ${result.recipients_count} project user${result.recipients_count !== 1 ? "s" : ""}`,
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      }

      notification.error({
        message: "Failed to send notification",
        description: errorMessage,
      });
    } finally {
      setNotifyingSubmittalId(null);
      setSubmittalToNotify(null);
    }
  };

  const handleDownloadAttachments = async (record, downloadType = "zip") => {
    const key = "downloadingSubmittal";
    const isPdf = downloadType === "pdf";
    const isMostRecent = downloadType === "most-recent";
    const formatLabel = isMostRecent
      ? "Most Recent Attachment(s)"
      : isPdf
        ? "PDF"
        : "ZIP";
    const fileExt = isPdf ? "pdf" : "zip";
    try {
      message.loading({
        content: isPdf
          ? "Requesting Submittal PDF report..."
          : `Preparing ${formatLabel}...`,
        key,
        duration: 0,
      });

      const result = await downloadSubmittalAttachments(record.id, downloadType);

      if (isPdf) {
        message.destroy(key);
        notification.info({
          message:
            result?.message ||
            "The report is being prepared. We will email it to you when it is ready.",
        });
        return;
      }

      // Build filename: "{spec}-{number}.{revision} {title}.{zip|pdf}"
      // e.g. "23 00 00-3.0 Fire Pump Data Sheet.zip"
      // The backend's build_submittal_attachments_download_filename builds the same name for
      // Content-Disposition; keep both in sync if this format ever changes.
      const sanitize = (value) =>
        String(value ?? "")
          .replace(/[<>:"/\\|?*]/g, "")
          .trim();
      const specNo = sanitize(
        record?.specification_section_details?.section_number,
      );
      const title = sanitize(record?.title) || "Submittal";
      const prefix = specNo ? `${specNo}-` : "";
      const filename = `${prefix}${record?.number}.${record?.revision} ${title}.${fileExt}`;

      downloadBlob(result, filename);

      message.destroy(key);
      notification.success({
        message: `Submittal attachments ${formatLabel} downloaded successfully`,
      });
    } catch (error) {
      console.error(error);
      message.destroy(key);

      // Backend wraps errors as { detail: { code, message } }; the body arrives
      // as a Blob because responseType is "blob". Pull out the human message.
      const extractMessage = (parsed) => {
        const detail = parsed?.detail;
        if (typeof detail === "string") return detail;
        if (detail && typeof detail.message === "string") return detail.message;
        if (typeof parsed?.message === "string") return parsed.message;
        return null;
      };

      let serverMessage = null;
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          serverMessage = extractMessage(JSON.parse(await data.text()));
        } catch (_) {
          // keep serverMessage null
        }
      } else if (data) {
        serverMessage = extractMessage(data);
      }

      // A submittal with no attachments is an expected, benign outcome — show a
      // soft info message rather than a red error toast.
      if (error.response?.status === 404) {
        notification.info({
          message:
            serverMessage ||
            (isMostRecent
              ? "Most recent response has no attachments to download"
              : "This submittal has no attachments to download"),
        });
        return;
      }

      notification.error({
        message: isPdf
          ? "Failed to request Submittal PDF report"
          : `Failed to download submittal attachments ${formatLabel}`,
        description: serverMessage || error.message || undefined,
      });
    }
  };

  const updateStatusMutation = useMutation({
    mutationFn: ({ submittalId, status }) =>
      updateSubmittal(submittalId, { status }, false),
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleStatusChange = (submittalId, newStatus) => {
    updateStatusMutation.mutate({ submittalId, status: newStatus });
  };

  const archiveMutation = useMutation({
    mutationFn: ({ submittalId, archived }) =>
      archiveSubmittal(submittalId, archived),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["submittals", projectId]);
      notification.success({
        message: variables.archived
          ? "Submittal archived"
          : "Submittal unarchived",
      });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update archive status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleArchiveToggle = (submittalId, archived) => {
    archiveMutation.mutate({ submittalId, archived });
  };

  const handleQuickRevision = async (submittalId) => {
    if (PAYMENT_PROJECTS_LIST.includes(projectId)) {
      notification.warning({
        message: "Your project has outstanding invoices.",
        description:
          "Your existing data will remain available. To create new Submittals, please have your project administrator complete the payment.",
      });
      return;
    }
    try {
      // Get the original submittal
      const originalSubmittal = await getSubmittalById(submittalId);

      // Prepare initial data for the modal
      const initialData = {
        title: originalSubmittal.title,
        description: originalSubmittal.description || "",
        number: originalSubmittal.number,
        revision: (originalSubmittal.revision || 0) + 1,
        specification_section_id:
          originalSubmittal.specification_section_id || null,
        submittal_manager_id: originalSubmittal.submittal_manager_id,
        received_from_id: originalSubmittal.received_from_id || null,
        responsible_contractor_id:
          originalSubmittal.responsible_contractor_id || null,
        submitted_for: originalSubmittal.submitted_for || "For Approval",
        priority: originalSubmittal.priority || "Medium",
        issue_date: dayjs(), // Quick revisions start with today's issue date
        due_date: null,
        released_on: null,
        estimated_delivery_date: null,
        distribution_list: originalSubmittal.distribution_list || [],
        steps: originalSubmittal.steps
          ? originalSubmittal.steps.map((step) => ({
              reviews: step.reviews
                ? step.reviews.map((review) => review.reviewer_id)
                : [],
            }))
          : [],
        attachments: [],
        ...(selectedProject?.use_custom_terminology && {
          type: "Submittal",
          edms: originalSubmittal.edms || null,
        }),
      };

      setRevisionInitialData(initialData);
      setIsCreateModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Failed to create quick revision",
        description: error.response?.data?.detail || error.message,
      });
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setSearchTexts((prev) => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, confirm, dataIndex)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const cellValue = record[dataIndex];
      if (cellValue !== undefined && cellValue !== null) {
        return cellValue.toString().toLowerCase().includes(value.toLowerCase());
      }
      return false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchTexts[dataIndex] || ""]}
          autoEscape
          textToHighlight={
            text !== undefined && text !== null ? text.toString() : ""
          }
        />
      ) : (
        text
      ),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const getColumnMultiSelectProps = (dataIndex, options) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      const FilterDropdownContent = () => {
        const [isDropdownOpen, setIsDropdownOpen] = useState(false);

        const handleConfirm = () => {
          confirm();
          setFilters((prev) => ({
            ...prev,
            [dataIndex]: selectedKeys,
          }));
        };

        return (
          <div
            style={{
              padding: 8,
              minWidth: 200,
              maxWidth: 300,
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Select
              mode="multiple"
              placeholder={`Select ${dataIndex}`}
              value={selectedKeys}
              onChange={(values) => setSelectedKeys(values)}
              style={{
                width: "100%",
                marginBottom: 8,
              }}
              options={options}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+${omittedValues.length} more`
              }
              dropdownStyle={{
                maxWidth: 300,
              }}
              onOpenChange={(open) => setIsDropdownOpen(open)}
              suffixIcon={
                isDropdownOpen ? (
                  <UpOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirm();
                    }}
                    style={{
                      cursor: "pointer",
                      color: "#1677ff",
                    }}
                  />
                ) : (
                  <DownOutlined
                    style={{
                      color: "#1677ff",
                    }}
                  />
                )
              }
            />
            <Space>
              <Button
                type="primary"
                onClick={handleConfirm}
                size="small"
                style={{
                  width: 90,
                }}
              >
                OK
              </Button>
              <Button
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                  setFilters((prev) => {
                    const newFilters = { ...prev };
                    delete newFilters[dataIndex];
                    return newFilters;
                  });
                }}
                size="small"
                style={{
                  width: 90,
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      };

      return <FilterDropdownContent />;
    },
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const cellValue = record[dataIndex];
      if (cellValue !== undefined && cellValue !== null) {
        return cellValue.toString() === value;
      }
      return false;
    },
    filteredValue: filters[dataIndex] || null,
  });

  const handleClearAllFilters = () => {
    setSearchedColumns([]);
    setSearchTexts({});
    setFilters({});
    setMasterSearch("");
    setSortField(null);
    setSortOrder("asc");
    setTableKey((prev) => prev + 1);
    clearModuleSettings(projectId, "submittals");
  };

  const handleQuickFilterToggle = (filterKey, kind) => {
    setFilters((prev) => toggleQuickFilter(prev, filterKey, kind));
    setPagination((prev) => ({ ...prev, current: 1 }));
    setTableKey((prev) => prev + 1);
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: (
        <SortableHeader
          title="Spec Section"
          sortField="formatted_spec_section"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "formatted_spec_section",
      key: "formatted_spec_section",
      isVisible: visibleColumns.includes("formatted_spec_section"),
      ...getColumnSearchProps("formatted_spec_section"),
    },
    {
      title: (
        <SortableHeader
          title="Number"
          sortField="formatted_number"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "formatted_number",
      key: "formatted_number",
      isVisible: visibleColumns.includes("formatted_number"),
      ...getColumnSearchProps("formatted_number"),
    },
    {
      title: (
        <SortableHeader
          title="Rev"
          sortField="revision"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "revision",
      key: "revision",
      isVisible: visibleColumns.includes("revision"),
      ...getColumnSearchProps("revision"),
    },
    {
      title: (
        <SortableHeader
          title="Title"
          sortField="title"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      ...getColumnSearchProps("title"),
    },
    {
      title: (
        <SortableHeader
          title="Submitted For"
          sortField="submitted_for"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "submitted_for",
      key: "submitted_for",
      isVisible: visibleColumns.includes("submitted_for"),
      ...getColumnMultiSelectProps("submitted_for", [
        { label: "For Approval", value: "For Approval" },
        { label: "For Record", value: "For Record" },
      ]),
    },
    {
      title: "From",
      dataIndex: "formatted_received_from",
      key: "formatted_received_from",
      isVisible: visibleColumns.includes("formatted_received_from"),
      ...getColumnSearchProps("formatted_received_from"),
    },
    {
      title: "Ball in Court",
      dataIndex: "formatted_ball_in_courts",
      key: "ball_in_court",
      isVisible: visibleColumns.includes("ball_in_court"),
      ...getColumnSearchProps("formatted_ball_in_courts"),
    },
    {
      title: (
        <SortableHeader
          title="Issue Date"
          sortField="issue_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "issue_date",
      key: "issue_date",
      isVisible: visibleColumns.includes("issue_date"),
      ...getColumnDateRangeProps("issue_date", "Issue Date"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Due Date"
          sortField="due_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "due_date",
      key: "due_date",
      isVisible: visibleColumns.includes("due_date"),
      ...getColumnDateRangeProps("due_date", "Due Date"),
      render: (text, record) => (
        <DueDatePill dateString={text} status={record.status} />
      ),
    },
    {
      title: "Most Rec. Resp. Date",
      dataIndex: "most_recent_response_date",
      key: "most_recent_response_date",
      isVisible: visibleColumns.includes("most_recent_response_date"),
      render: (text) => formatDateTimeLocal(text),
    },
    {
      title: (
        <SortableHeader
          title="Released On"
          sortField="released_on"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "released_on",
      key: "released_on",
      isVisible: trackMaterialRelease && visibleColumns.includes("released_on"),
      ...getColumnDateRangeProps("released_on", "Released On"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Est. Delivery"
          sortField="estimated_delivery_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "estimated_delivery_date",
      key: "estimated_delivery_date",
      isVisible:
        trackMaterialRelease &&
        visibleColumns.includes("estimated_delivery_date"),
      ...getColumnDateRangeProps("estimated_delivery_date", "Est. Delivery"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Status"
          sortField="status"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      ...getColumnMultiSelectProps("status", [
        { label: "Open", value: "Open" },
        { label: "Closed", value: "Closed" },
        { label: "Draft", value: "Draft" },
      ]),
      render: (status, record) => {
        const getStatusColor = (status) => {
          switch (status) {
            case "Open":
              return "orange";
            case "Closed":
              return "default";
            case "Draft":
              return "blue";
            default:
              return "default";
          }
        };

        // Consultants and subcontractors can only view status, not change it
        if (!isOwnerOrManager) {
          return (
            <Space size={4}>
              <Tag color={getStatusColor(status)} className="m-0">
                {status}
              </Tag>
              {record.archived && <Tag className="m-0">Archived</Tag>}
            </Space>
          );
        }

        // Owners and managers can change status
        return (
          <Space size={4}>
            <Select
              value={status}
              onChange={(value) => handleStatusChange(record.id, value)}
              className="status-select-tag w-[120px]"
              loading={updateStatusMutation.isPending}
              disabled={updateStatusMutation.isPending}
              popupRender={(menu) => menu}
              suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
            >
              <Select.Option value="Open">
                <Tag color="orange" className="m-0 w-full text-center block">
                  Open
                </Tag>
              </Select.Option>
              <Select.Option value="Closed">
                <Tag color="default" className="m-0 w-full text-center block">
                  Closed
                </Tag>
              </Select.Option>
              <Select.Option value="Draft">
                <Tag color="blue" className="m-0 w-full text-center block">
                  Draft
                </Tag>
              </Select.Option>
            </Select>
            {record.archived && <Tag className="m-0">Archived</Tag>}
          </Space>
        );
      },
    },

    {
      title: (
        <SortableHeader
          title="Closed Date"
          sortField="closed_at_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "closed_at_date",
      key: "closed_at_date",
      isVisible: visibleColumns.includes("closed_at_date"),
      ...getColumnDateRangeProps("closed_at_date", "Closed Date"),
      render: (text, record) => {
        if (record.status !== "Closed" || !text) {
          return "";
        }
        return formatDate(text);
      },
    },

    {
      title: (
        <SortableHeader
          title="Review Status"
          sortField="review_status"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "review_status",
      key: "review_status",
      isVisible: visibleColumns.includes("review_status"),
      ...getColumnMultiSelectProps("review_status", [
        { label: "Pending", value: "Pending" },
        { label: "Approved", value: "Approved" },
        { label: "Approved as Noted", value: "Approved as Noted" },
        { label: "Rejected", value: "Rejected" },
        { label: "Revise and Resubmit", value: "Revise and Resubmit" },
        { label: "Void", value: "Void" },
        { label: "For Record Only", value: "For Record Only" },
        { label: "Reviewed", value: "Reviewed" },
        { label: "Submitted", value: "Submitted" },
      ]),
      render: (reviewStatus) => (
        <ReviewStatusTag status={reviewStatus} emptyLabel="-" />
      ),
    },
    {
      title: (
        <SortableHeader
          title="Priority"
          sortField="priority"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "priority",
      key: "priority",
      isVisible: visibleColumns.includes("priority"),
      ...getColumnSearchProps("priority", "select", [
        { label: "High", value: "High" },
        { label: "Medium", value: "Medium" },
        { label: "Low", value: "Low" },
      ]),
      render: (priority) => {
        if (!priority) return <span>-</span>;

        const priorityColorMap = {
          High: "red",
          Medium: "blue",
          Low: "green",
        };

        const color = priorityColorMap[priority] || "default";

        return (
          <Tag color={color} style={{ fontWeight: "bold" }}>
            {priority}
          </Tag>
        );
      },
    },
    ...(selectedProject?.use_custom_terminology
      ? [
          {
            title: (
              <SortableHeader
                title="EDMS"
                sortField="edms"
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSort={handleSort}
              />
            ),
            dataIndex: "edms",
            key: "edms",
            isVisible: visibleColumns.includes("edms"),
            ...getColumnSearchProps("edms"),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      isVisible: true,
      render: (_, record) => {
        if (!isOwnerOrManager) {
          const isInBallInCourt = record.ball_in_court_ids?.some(
            (id) => String(id) === String(currentUser?.id),
          );
          const buttonText = isInBallInCourt ? "Respond" : "View";
          return (
            <Button
              onClick={() => handleViewClick(record?.id)}
              size="medium"
              style={{ width: "90px" }}
              type={isInBallInCourt ? "primary" : "default"}
              ghost={isInBallInCourt}
            >
              {buttonText}
            </Button>
          );
        }

        // For owners and managers, show dropdown with all options
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1" onClick={() => handleViewClick(record?.id)}>
                  <Button className="w-full">View</Button>
                </Menu.Item>
                <Menu.Item key="2" onClick={() => handleUpdateClick(record)}>
                  <Button className="w-full">Update</Button>
                </Menu.Item>
                <Menu.Item
                  key="3"
                  onClick={() => handleQuickRevision(record.id)}
                >
                  <Button className="w-full">Create Revision</Button>
                </Menu.Item>
                <Menu.Item key="4">
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="zip"
                          onClick={() =>
                            handleDownloadAttachments(record, "zip")
                          }
                        >
                          ZIP
                        </Menu.Item>
                        <Menu.Item
                          key="pdf"
                          onClick={() =>
                            handleDownloadAttachments(record, "pdf")
                          }
                        >
                          PDF
                        </Menu.Item>
                        <Menu.Item
                          key="most-recent"
                          onClick={() =>
                            handleDownloadAttachments(record, "most-recent")
                          }
                        >
                          Most Recent Attachment(s)
                        </Menu.Item>
                      </Menu>
                    }
                    placement="rightTop"
                    trigger={["hover"]}
                  >
                    <Button
                      className="w-full"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <span>Download</span>
                      <RightOutlined style={{ fontSize: 10 }} />
                    </Button>
                  </Dropdown>
                </Menu.Item>
                <Menu.Item
                  key="notify"
                  onClick={() => handleNotifyUsers(record)}
                  disabled={notifyingSubmittalId === record.id}
                >
                  <Button
                    className="w-full"
                    loading={notifyingSubmittalId === record.id}
                  >
                    Notify Users
                  </Button>
                </Menu.Item>
                <Menu.Item
                  key="archive"
                  onClick={() =>
                    handleArchiveToggle(record.id, !record.archived)
                  }
                >
                  <Tooltip
                    title={
                      record.archived
                        ? "Unarchiving shows this submittal in the list again."
                        : "Archiving hides this submittal from the list and exports. Switch the Active/Archived/All filter to see it."
                    }
                  >
                    <Button className="w-full">
                      {record.archived ? "Unarchive" : "Archive"}
                    </Button>
                  </Tooltip>
                </Menu.Item>
                <Menu.Item key="5">
                  <Popconfirm
                    title="Are you sure you want to delete this submittal?"
                    onConfirm={() => handleDeleteClick(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button className="w-full" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EditOutlined />} size="medium"></Button>
          </Dropdown>
        );
      },
    },
  ];

  const content = (
    <ColumnSelectorPopover
      columnOptions={selectableColumnOptions}
      orderedKeys={tempOrderedColumns}
      selectedKeys={tempSelection}
      onOrderedKeysChange={setTempOrderedColumns}
      onSelectedKeysChange={setTempSelection}
      onSave={handleSave}
      onCancel={handleCancel}
      allowReorder={true}
    />
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Submittals</h1>
      <div className="flex gap-4 mb-4">
        {isOwnerOrManager && (
          <Button
            type="primary"
            onClick={() => {
              if (PAYMENT_PROJECTS_LIST.includes(projectId)) {
                notification.warning({
                  message: "Your project has outstanding invoices.",
                  description:
                    "Your existing data will remain available. To create new Submittals, please have your project administrator complete the payment.",
                });
                return;
              }
              setIsCreateModalVisible(true);
            }}
          >
            Create
          </Button>
        )}
        <QuickFilterButtons
          filters={filters}
          onToggle={handleQuickFilterToggle}
        />
        <Button icon={<DownloadOutlined />} onClick={handleExportPDF}>
          Export PDF
        </Button>
        {isSpecialProject && (
          <Button onClick={handleDownloadSpecialTable}>Custom Report</Button>
        )}

        <Popover
          content={isLoadingColumns ? <Skeleton active /> : content}
          title="Select Columns to Display"
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(visible) => {
            setIsPopoverOpen(visible);
            if (visible) {
              initializeColumnSelector();
            }
          }}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
        <Input.Search
          placeholder="Search across all fields..."
          defaultValue=""
          onSearch={(value) => setMasterSearch(value)}
          style={{ width: 400 }}
          allowClear
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              className="!inline-flex !items-center !justify-center"
            />
          }
        />
        <Segmented
          className="ml-auto"
          value={archivedFilter}
          onChange={setArchivedFilter}
          options={[
            { label: "Active", value: "active" },
            { label: "Archived", value: "archived" },
            { label: "All", value: "all" },
          ]}
        />
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearAllFilters}
        >
          Clear All Filters
        </Button>
      </div>

      <Table
        columns={orderColumnsByKeys(
          columns,
          pinKeyLast(visibleColumns, "action"),
        ).filter((col) => col.isVisible === true)}
        dataSource={submittalData?.submittals}
        rowKey="id"
        key={tableKey}
        loading={isLoading || deleteMutation.isPending}
        rowClassName={(record) => (record.archived ? "opacity-60" : "")}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: submittalData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateSubmittalModal
        visible={isCreateModalVisible}
        onClose={() => {
          setIsCreateModalVisible(false);
          setRevisionInitialData(null);
        }}
        initialData={revisionInitialData}
      />
      {selectedSubmittalId && (
        <UpdateSubmittalModal
          visible={isUpdateModalVisible}
          onClose={() => {
            setIsUpdateModalVisible(false);
            setSelectedSubmittalId(null);
          }}
          submittalId={selectedSubmittalId}
        />
      )}
      {selectedSubmittalId && (
        <ViewSubmittalModal
          visible={isViewModalVisible}
          onClose={
            magicLoginActive || isMagicLogin
              ? () => {}
              : () => {
                  setIsViewModalVisible(false);
                }
          }
          submittalId={selectedSubmittalId}
          isLocked={magicLoginActive || isMagicLogin}
          scrollToRespond={true}
        />
      )}
      <NotifyUsersModal
        visible={isNotifyUsersModalVisible}
        onClose={() => {
          notifyDefaultRecipientsFetchSeqRef.current += 1;
          setIsNotifyUsersModalVisible(false);
          setSubmittalToNotify(null);
          setNotifyDefaultUserIds([]);
          setNotifyDefaultUserIdsLoading(false);
        }}
        folder={submittalToNotify}
        itemName={submittalToNotify?.title}
        defaultUserIds={notifyDefaultUserIds}
        defaultUserIdsLoading={notifyDefaultUserIdsLoading}
        onNotify={handleSendNotification}
      />
    </div>
  );
};

export default Submittals;
